
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { fadeInVariants } from '@/lib/animations';
import { useYoungSchemasTest } from '@/hooks/useYoungSchemasTest';
import { AlertCircle, RefreshCw } from 'lucide-react';
import ProgressBar from '@/components/personality/ProgressBar';
import YoungSchemasResults from './YoungSchemasResults';

export default function YoungSchemasQuestionnaire() {
  const navigate = useNavigate();
  const { 
    questions, 
    currentQuestion, 
    currentIndex, 
    answers, 
    progress, 
    isCompleted,
    isLoading,
    error,
    serviceAvailable,
    answerQuestion, 
    previousQuestion, 
    getCurrentAnswer,
    testResults,
    resetTest
  } = useYoungSchemasTest();
  
  const [notes, setNotes] = useState('');

  const handleAnswerSelect = (score: number) => {
    answerQuestion(score);
  };

  const handleRetry = () => {
    // Refresh the page to reinitialize services
    window.location.reload();
  };

  // Show service unavailable or general errors
  if (!serviceAvailable || error) {
    return (
      <motion.div
        variants={fadeInVariants}
        initial="initial"
        animate="animate"
        className="max-w-3xl mx-auto"
      >
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3 text-red-700">
              <AlertCircle className="h-6 w-6" />
              <div>
                <h3 className="font-semibold">
                  {!serviceAvailable ? 'Teste Indisponível' : 'Ocorreu um erro'}
                </h3>
                <p className="text-sm mt-1">
                  {error || 'Não foi possível carregar o teste. Tente recarregar a página.'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleRetry} className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Tentar Novamente</span>
              </Button>
              <Button variant="outline" onClick={() => navigate('/testes-personalidade')}>
                Voltar aos Testes
              </Button>
              {serviceAvailable && (
                <Button variant="outline" onClick={resetTest}>
                  Reiniciar Teste
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (isCompleted && testResults) {
    return <YoungSchemasResults results={testResults} />;
  }

  if (!currentQuestion) {
    return (
      <motion.div
        variants={fadeInVariants}
        initial="initial"
        animate="animate"
        className="max-w-3xl mx-auto"
      >
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="py-8">
              <p className="text-lg">Carregando questão...</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={fadeInVariants}
      initial="initial"
      animate="animate"
      className="max-w-3xl mx-auto"
    >
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-medium">Seu progresso</h2>
          <span className="text-sm font-medium">{progress}% concluído</span>
        </div>
        <ProgressBar progress={progress} />
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h3 className="text-xl font-medium">
              {currentIndex + 1}. {currentQuestion.text}
            </h3>
            
            <div className="grid grid-cols-6 gap-2 text-center text-sm mt-8">
              <div>Completamente falso</div>
              <div>Majoritariamente falso</div>
              <div>Mais falso que verdadeiro</div>
              <div>Mais verdadeiro que falso</div>
              <div>Majoritariamente verdadeiro</div>
              <div>Completamente verdadeiro</div>
            </div>
            
            <div className="grid grid-cols-6 gap-2">
              {[1, 2, 3, 4, 5, 6].map((score) => {
                const isSelected = getCurrentAnswer() === score;
                return (
                  <Button
                    key={score}
                    variant={isSelected ? "default" : "outline"}
                    onClick={() => handleAnswerSelect(score)}
                    className={`h-12 ${isSelected ? "bg-primary text-primary-foreground" : ""}`}
                    disabled={isLoading}
                  >
                    {score}
                  </Button>
                );
              })}
            </div>
          </div>

          {isLoading && (
            <div className="text-center py-4 flex items-center justify-center space-x-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Carregando...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={previousQuestion}
          disabled={currentIndex === 0 || isLoading}
        >
          Voltar
        </Button>
        
        <div className="text-sm text-muted-foreground">
          Questão {currentIndex + 1} de {questions.length}
        </div>
      </div>

      <Separator className="my-6" />

      <div className="mt-6">
        <h3 className="text-md font-medium mb-2">Notas adicionais (opcional)</h3>
        <Textarea 
          placeholder="Digite qualquer observação ou reflexão sobre suas respostas..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="h-24"
          disabled={isLoading}
        />
      </div>
    </motion.div>
  );
}
